/**
 * API endpoint constants to avoid hardcoded URLs throughout the application
 */

/**
 * Base API configuration
 */
export const API_CONFIG = {
  BASE_URL: '/api',
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second initial delay
} as const;

/**
 * Authentication endpoints
 */
export const AUTH_ENDPOINTS = {
  LOGIN: '/v1/auth/login',
  LOGIN_2FA: '/v1/auth/login/2fa',
  LOGIN_OIDC: '/v1/auth/login/oidc',
  LOGOUT: '/v1/auth/logout',
  REFRESH: '/v1/auth/refresh',
  ME: '/v1/auth/me',
  CHANGE_PASSWORD: '/v1/auth/change-password',
} as const;

/**
 * Two-Factor Authentication endpoints
 */
export const TWO_FACTOR_ENDPOINTS = {
  SETUP: '/v1/2fa/setup',
  VERIFY_SETUP: '/v1/2fa/verify-setup',
  DISABLE: '/v1/2fa/disable',
  VERIFY: '/v1/2fa/verify',
  VERIFY_BACKUP: '/v1/2fa/verify-backup',
  BACKUP_CODES: '/v1/2fa/backup-codes',
  STATUS: '/v1/2fa/status',
  QR_CODE: '/v1/2fa/setup/qrcode',
} as const;

/**
 * User management endpoints
 */
export const USER_ENDPOINTS = {
  BASE: '/v1/users',
  BY_ID: (id: string) => `/v1/users/${id}`,
  RESET_PASSWORD: (id: string) => `/v1/users/${id}/reset-password`,
  BULK_UPDATE: '/v1/users/bulk-update',
  BULK_DEACTIVATE: '/v1/users/bulk-deactivate',
  BULK_RESET_PASSWORD: '/v1/users/bulk-reset-password',
} as const;

/**
 * Associate endpoints
 */
export const ASSOCIATE_ENDPOINTS = {
  BASE: '/v1/associates',
  BY_ID: (id: string) => `/v1/associates/${id}`,
  PHOTO: (id: string) => `/v1/associates/${id}/photo`,
  ID_CARD: (id: string) => `/v1/associates/${id}/id-card`,
  BULK_UPDATE: '/v1/associates/bulk-update',
  BULK_DELETE: '/v1/associates/bulk-delete',
  BULK_INVITE: '/v1/associates/bulk-invite',
  BULK_EXPORT: '/v1/associates/bulk-export',
} as const;

/**
 * Interview endpoints
 */
export const INTERVIEW_ENDPOINTS = {
  BASE: '/v1/interviews',
  BY_ID: (id: string) => `/v1/interviews/${id}`,
  START: '/v1/interviews/start',
  STATUS: (id: string) => `/v1/interviews/${id}/status`,
  COMPLETE: (id: string) => `/v1/interviews/${id}/complete`,
  SUBMIT_RESPONSE: (id: string) => `/v1/interviews/${id}/submit-response`,
  UPLOAD_RESPONSE: (id: string) => `/v1/interviews/${id}/upload-response`,
  CANDIDATE_INFO: (id: string) => `/v1/interviews/${id}/candidate-info`,
  IDENTITY_DOCUMENTS: (id: string) => `/v1/interviews/${id}/identity-documents`,
  IQ_TEST: (id: string) => `/v1/interviews/${id}/iq-test`,
  IQ_TEST_ADMIN: (id: string) => `/v1/interviews/${id}/iq-test/admin`,
  REVIEW_QUEUE: '/v1/interviews/review-queue',
  REVIEW: (id: string) => `/v1/interviews/${id}/review`,
  SNIPPETS: (id: string) => `/v1/interviews/${id}/snippets`,
  TRANSCRIPT: (id: string) => `/v1/interviews/${id}/transcript`,
  PLAYBACK: (id: string) => `/v1/interviews/${id}/playback`,
  INSIGHTS: (id: string) => `/v1/interviews/${id}/insights`,
  HIGHLIGHTS: (id: string) => `/v1/interviews/${id}/highlights`,
  DASHBOARD_STATS: '/v1/interviews/dashboard/stats',
  CLAIM: (id: string) => `/v1/interviews/${id}/claim`,
  RELEASE: (id: string) => `/v1/interviews/${id}/release`,
  CLAIM_NEXT: '/v1/interviews/queue/claim-next',
  VIDEO_CHUNK: (id: string) => `/v1/video/interviews/${id}/video/chunk`,
  VIDEO_FINALIZE: (id: string) => `/v1/video/interviews/${id}/video/finalize`,
} as const;

/**
 * Question endpoints
 */
export const QUESTION_ENDPOINTS = {
  BASE: '/v1/questions',
  BY_ID: (id: string) => `/v1/questions/${id}`,
} as const;

/**
 * Invite endpoints
 */
export const INVITE_ENDPOINTS = {
  BASE: '/v1/invites',
  BY_ID: (id: string) => `/v1/invites/${id}`,
  BULK: '/v1/invites/bulk',
  RESEND: (id: string) => `/v1/invites/${id}/resend`,
  CANCEL: (id: string) => `/v1/invites/${id}/cancel`,
  STATS: '/v1/invites/stats',
} as const;

/**
 * Report endpoints
 */
export const REPORT_ENDPOINTS = {
  ASSOCIATES: '/v1/reports/associates',
  INTERVIEWS: '/v1/reports/interviews',
  REVIEWS: '/v1/reports/reviews',
  FRAUD: '/v1/reports/fraud',
  ORGANIZATION_SUMMARY: '/v1/reports/organization-summary',
} as const;

/**
 * Analytics endpoints
 */
export const ANALYTICS_ENDPOINTS = {
  OVERVIEW: '/v1/analytics/overview',
  TRENDS: '/v1/analytics/trends',
  ORGANIZATIONS: '/v1/analytics/organizations',
  REVIEWERS: '/v1/analytics/reviewers',
  FRAUD: '/v1/analytics/fraud',
  QUESTIONS: '/v1/analytics/questions',
  CALIBRATION: '/v1/analytics/reviewer-calibration',
} as const;

/**
 * Analytics Reports endpoints (Custom Dashboards)
 */
export const ANALYTICS_REPORTS_ENDPOINTS = {
  BASE: '/v1/analytics-reports',
  BY_ID: (id: string) => `/v1/analytics-reports/${id}`,
  TEMPLATES: '/v1/analytics-reports/templates',
  FAVORITES: '/v1/analytics-reports/favorites',
  EXECUTE: (id: string) => `/v1/analytics-reports/${id}/execute`,
  EXECUTIONS: (id: string) => `/v1/analytics-reports/${id}/executions`,
  SCHEDULE: (id: string) => `/v1/analytics-reports/${id}/schedule`,
  PREFERENCES: (id: string) => `/v1/analytics-reports/${id}/preferences`,
  DUPLICATE: (id: string) => `/v1/analytics-reports/${id}/duplicate`,
  EXPORT: (id: string) => `/v1/analytics-reports/${id}/export`,
  AGGREGATIONS: '/v1/analytics-reports/aggregations',
  AGGREGATIONS_COMBINED: '/v1/analytics-reports/aggregations/combined',
  AGGREGATIONS_REFRESH: '/v1/analytics-reports/aggregations/refresh',
} as const;

/**
 * Audit endpoints
 */
export const AUDIT_ENDPOINTS = {
  LOGS: '/v1/audit/logs',
  LOG_BY_ID: (id: string) => `/v1/audit/logs/${id}`,
  ACTIONS: '/v1/audit/actions',
  STATS: '/v1/audit/stats',
  ENTITY_HISTORY: (entityType: string, entityId: string) =>
    `/v1/audit/entity/${entityType}/${entityId}`,
} as const;

/**
 * Backup endpoints
 */
export const BACKUP_ENDPOINTS = {
  CREATE: '/v1/backup/create',
  LIST: '/v1/backup/list',
  STATUS: (id: string) => `/v1/backup/status/${id}`,
  DOWNLOAD: (id: string) => `/v1/backup/download/${id}`,
  RESTORE: (id: string) => `/v1/backup/restore/${id}`,
  DELETE: (id: string) => `/v1/backup/${id}`,
} as const;

/**
 * Preferences endpoints
 */
export const PREFERENCES_ENDPOINTS = {
  BASE: '/v1/preferences',
  RESET: '/v1/preferences/reset',
} as const;

/**
 * Performance monitoring endpoints
 */
export const PERFORMANCE_ENDPOINTS = {
  METRICS: '/v1/performance/metrics',
  REPORT: '/v1/performance/report',
  BUDGETS: '/v1/performance/budgets',
  VIOLATIONS: '/v1/performance/violations',
} as const;

/**
 * Settings endpoints
 */
export const SETTINGS_ENDPOINTS = {
  ORGANIZATION_BY_ID: (id: string) => `/v1/organizations/${id}`,
  ORGANIZATION_SETTINGS: (id: string) => `/v1/organizations/${id}/settings`,
  EMAIL_TEMPLATES: '/v1/email/templates',
  EMAIL_TEMPLATE_BY_TYPE: (type: string) => `/v1/email/templates/${type}`,
} as const;

/**
 * Helper function to build query string from params
 */
export function buildQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach((item) => searchParams.append(key, String(item)));
      } else {
        searchParams.append(key, String(value));
      }
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Helper function to build URL with path parameters
 */
export function buildUrl(
  template: string | (((...args: any[]) => string)),
  ...params: any[]
): string {
  if (typeof template === 'function') {
    return template(...params);
  }
  return template;
}
